import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { formatCurrency } from '@/utils';
import { format } from 'date-fns';
import { parseDate } from '@/lib/localizedDateUtils';
import { Loader2, AlertCircle } from 'lucide-react';
import type { UnlinkedTransaction } from '@/hooks/usePaymentTransactionsByDeal';

interface Payment {
  id: string;
  payment_number: string;
  amount: number;
  balance: number;
  due_date: string;
  status: string;
}

interface LinkTransactionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: UnlinkedTransaction | null;
  payments: Payment[];
  onSuccess: () => void;
}

export function LinkTransactionModal({
  open,
  onOpenChange,
  transaction,
  payments,
  onSuccess
}: LinkTransactionModalProps) {
  const { t } = useTranslation();
  const [selectedPayments, setSelectedPayments] = useState<string[]>([]);
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [isLinking, setIsLinking] = useState(false);

  // Filter to show only unpaid/partially paid payments with balance > 0
  const availablePayments = useMemo(() => {
    return payments
      .filter(p => p.balance > 0 && p.status !== 'paid' && p.status !== 'cancelled')
      .sort((a, b) => (parseDate(a.due_date)?.getTime() || 0) - (parseDate(b.due_date)?.getTime() || 0));
  }, [payments]);

  const transactionAmount = transaction?.amount || 0;

  // Calculate total allocated
  const totalAllocated = useMemo(() => {
    return selectedPayments.reduce((sum, id) => {
      return sum + (parseFloat(allocations[id]) || 0);
    }, 0);
  }, [selectedPayments, allocations]);

  const remainingToAllocate = transactionAmount - totalAllocated;

  const handlePaymentSelection = (paymentId: string, checked: boolean) => {
    if (checked) {
      setSelectedPayments(prev => [...prev, paymentId]);
      // Auto-fill with remaining amount or payment balance, whichever is smaller
      const payment = availablePayments.find(p => p.id === paymentId);
      if (payment) {
        const autoAmount = Math.min(remainingToAllocate, payment.balance);
        if (autoAmount > 0) {
          setAllocations(prev => ({ ...prev, [paymentId]: autoAmount.toFixed(2) }));
        }
      }
    } else {
      setSelectedPayments(prev => prev.filter(id => id !== paymentId));
      setAllocations(prev => {
        const newAlloc = { ...prev };
        delete newAlloc[paymentId];
        return newAlloc;
      });
    }
  };

  const handleAllocationChange = (paymentId: string, value: string) => {
    setAllocations(prev => ({ ...prev, [paymentId]: value }));
  };

  const handleLink = async () => {
    if (!transaction || selectedPayments.length === 0) return;

    // Validate allocations
    const allocationsList = selectedPayments.map(id => ({
      paymentId: id,
      amount: parseFloat(allocations[id]) || 0
    })).filter(a => a.amount > 0);

    if (allocationsList.length === 0) {
      toast.error('Informe o valor a alocar em pelo menos uma parcela');
      return;
    }

    const totalToAllocate = allocationsList.reduce((sum, a) => sum + a.amount, 0);
    if (Math.abs(totalToAllocate - transactionAmount) > 0.01) {
      toast.error(`O total alocado (${formatCurrency(totalToAllocate)}) deve ser igual ao valor da transação (${formatCurrency(transactionAmount)})`);
      return;
    }

    setIsLinking(true);
    try {
      if (allocationsList.length === 1) {
        // Single allocation: just update the payment_id of the original transaction
        const { error } = await supabase
          .from('payment_transactions')
          .update({ payment_id: allocationsList[0].paymentId })
          .eq('id', transaction.id);

        if (error) throw error;
      } else {
        // Multiple allocations: create child transactions and delete original
        const baseTransaction = {
          deal_id: transaction.deal_id,
          contact_id: transaction.contact_id,
          transaction_date: transaction.transaction_date,
          currency: transaction.currency,
          payment_method: transaction.payment_method,
          reference_number: transaction.reference_number,
          notes: transaction.notes ? `${transaction.notes} (Alocação parcial)` : 'Alocação parcial de transação avulsa',
          status: transaction.status,
          stripe_payment_intent_id: transaction.stripe_payment_intent_id,
        };

        // Create new transactions for each allocation
        for (const alloc of allocationsList) {
          const { error: insertError } = await supabase
            .from('payment_transactions')
            .insert({
              ...baseTransaction,
              payment_id: alloc.paymentId,
              amount: alloc.amount,
            });

          if (insertError) throw insertError;
        }

        // Delete original unlinked transaction
        const { error: deleteError } = await supabase
          .from('payment_transactions')
          .delete()
          .eq('id', transaction.id);

        if (deleteError) throw deleteError;
      }

      toast.success('Transação vinculada com sucesso!');
      onSuccess();
      onOpenChange(false);
      
      // Reset state
      setSelectedPayments([]);
      setAllocations({});
    } catch (error: any) {
      console.error('Error linking transaction:', error);
      toast.error(`Erro ao vincular transação: ${error.message}`);
    } finally {
      setIsLinking(false);
    }
  };

  const handleClose = () => {
    setSelectedPayments([]);
    setAllocations({});
    onOpenChange(false);
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vincular Transação a Parcelas</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Transaction info */}
          <div className="p-3 bg-muted rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Valor da Transação</span>
              <span className="text-lg font-bold text-primary">
                {formatCurrency(transactionAmount)}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {format(parseDate(transaction.transaction_date)!, 'dd/MM/yyyy')} • {transaction.payment_method}
            </div>
          </div>

          {/* Allocation status */}
          <div className="flex items-center justify-between p-2 rounded bg-background border">
            <span className="text-sm">Total Alocado:</span>
            <div className="flex items-center gap-2">
              <span className={`font-medium ${Math.abs(remainingToAllocate) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
                {formatCurrency(totalAllocated)}
              </span>
              {remainingToAllocate > 0.01 && (
                <Badge variant="outline" className="text-amber-600 border-amber-300">
                  Falta: {formatCurrency(remainingToAllocate)}
                </Badge>
              )}
              {remainingToAllocate < -0.01 && (
                <Badge variant="outline" className="text-red-600 border-red-300">
                  Excede: {formatCurrency(Math.abs(remainingToAllocate))}
                </Badge>
              )}
            </div>
          </div>

          {/* Payment selection */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Selecione as Parcelas</Label>
            {availablePayments.length === 0 ? (
              <div className="flex items-center gap-2 p-4 bg-muted/50 rounded-lg text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Não há parcelas pendentes para vincular</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-[250px] overflow-y-auto border rounded-md p-2">
                {availablePayments.map(payment => {
                  const isSelected = selectedPayments.includes(payment.id);
                  return (
                    <div
                      key={payment.id}
                      className={`flex items-center gap-3 p-2 rounded transition-colors ${isSelected ? 'bg-primary/5 border border-primary/20' : 'hover:bg-muted/50'}`}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handlePaymentSelection(payment.id, !!checked)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{payment.payment_number}</span>
                          <span className="text-xs text-muted-foreground">
                            {format(parseDate(payment.due_date)!, 'dd/MM/yyyy')}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Saldo: {formatCurrency(payment.balance)}
                        </div>
                      </div>
                      {isSelected && (
                        <div className="w-28">
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max={payment.balance}
                            value={allocations[payment.id] || ''}
                            onChange={(e) => handleAllocationChange(payment.id, e.target.value)}
                            placeholder="Valor"
                            className="h-8 text-sm"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose} disabled={isLinking}>
            Cancelar
          </Button>
          <Button
            onClick={handleLink}
            disabled={isLinking || selectedPayments.length === 0 || Math.abs(remainingToAllocate) > 0.01}
          >
            {isLinking && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Vincular
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}